import * as Immutable from 'immutable';

// Utils
import { createToolbarList } from './toolbarAvailableToolsUtils';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { isTask } from '../../../../common/elements/utils/elementTypeUtils';
import { getSetsEqual } from './toolbarGeneralUtils';

// Types
import { ToolbarItemConfig, ToolbarList, ToolbarListType } from '../toolbarTypes';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { ElementType } from '../../../../common/elements/elementTypes';
import { ImMNCurrentUser } from '../../../user/currentUserSelector';

const TASK_ONLY_ELEMENT_TYPES = new Set([ElementType.TASK_TYPE]);
const EMPTY_LIST = Immutable.List<ImMNElement>();

const getSelectedParentTaskList = ({
    selectedElements,
    dispatchGetSelectedParentTaskList,
}: {
    selectedElements: Immutable.List<ImMNElement>;
    dispatchGetSelectedParentTaskList: (elementId: string) => ImMNElement | undefined;
}): Immutable.List<ImMNElement> => {
    const selectedTasks = selectedElements.filter(isTask);
    if (selectedTasks.size === 0) return EMPTY_LIST;

    const selectedTask = selectedTasks.first();
    if (!selectedTask) return EMPTY_LIST;

    const taskList = dispatchGetSelectedParentTaskList(getElementId(selectedTask));
    if (!taskList) return EMPTY_LIST;

    return Immutable.List([taskList]);
};

type ToolbarArgs = {
    selectedElements: Immutable.List<ImMNElement>;
    selectedElementIds: Immutable.List<string>;
    selectedChildElements: Immutable.List<ImMNElement>;
    editorKey: string | undefined;
    hasEditorSelection: boolean;
    permissions: number;
    currentUser: ImMNCurrentUser;
    dispatchGetSelectedParentTaskList: (elementId: string) => ImMNElement | undefined;
};

const ensureRequiredToolbarExists = (
    toolbarLists: ToolbarList[],
    requiredType: ToolbarListType,
    toolbarTypePriorities: ToolbarListType[],
    baseToolLists: Record<ToolbarListType, ToolbarItemConfig[]>,
    { selectedElements, selectedChildElements, currentUser, permissions, editorKey, hasEditorSelection }: ToolbarArgs,
): ToolbarList[] => {
    const requiredToolbar = createToolbarList(
        requiredType,
        toolbarTypePriorities,
        baseToolLists,
        selectedElements,
        selectedChildElements,
        currentUser,
        permissions,
        editorKey,
        hasEditorSelection,
    );

    // if the required toolbar has an id matching the id of existing toolbar, we don't need to add it
    if (toolbarLists.find((t) => t.id === requiredToolbar.id)) return toolbarLists;

    return [...toolbarLists, requiredToolbar];
};

/**
 * Ensures that the list of existing toolbars contains all supporting toolbars require for an incoming toolbar.
 * If any supporting toolbars are missing, they will be created and appended to the list.
 */
export const ensureSupportingToolbarListsExist = (
    existingToolbars: ToolbarList[],
    incomingToolbar: ToolbarList,
    toolbarTypePriorities: ToolbarListType[],
    baseToolLists: Record<ToolbarListType, ToolbarItemConfig[]>,
    args: ToolbarArgs,
): ToolbarList[] => {
    let toolbars = existingToolbars;

    // If the incoming toolbar is an "editing" toolbar, we need to ensure a "selected" toolbar exists
    if (incomingToolbar.type === ToolbarListType.EDITING || incomingToolbar.type === ToolbarListType.EDITING_RANGE) {
        toolbars = ensureRequiredToolbarExists(
            toolbars,
            ToolbarListType.SELECTED,
            toolbarTypePriorities,
            baseToolLists,
            args,
        );
    }

    // If the incoming toolbar is an "editing_range" toolbar, we need to ensure an "editing" toolbar exists
    if (incomingToolbar.type === ToolbarListType.EDITING_RANGE) {
        toolbars = ensureRequiredToolbarExists(
            toolbars,
            ToolbarListType.EDITING,
            toolbarTypePriorities,
            baseToolLists,
            args,
        );
    }

    // Special case: If the incoming editing toolbar is a TASK, we need to ensure a "selected" toolbar exists for the parent TASK_LIST
    if (
        incomingToolbar.type === ToolbarListType.EDITING &&
        getSetsEqual(incomingToolbar.elementTypes, TASK_ONLY_ELEMENT_TYPES)
    ) {
        const selectedParentTaskLists = getSelectedParentTaskList(args);
        if (selectedParentTaskLists.size === 0) return toolbars;

        toolbars = ensureRequiredToolbarExists(
            toolbars,
            ToolbarListType.SELECTED,
            toolbarTypePriorities,
            baseToolLists,
            {
                ...args,
                selectedElements: selectedParentTaskLists,
                selectedElementIds: selectedParentTaskLists.map(getElementId) as Immutable.List<string>,
            },
        );
    }

    return toolbars;
};
